import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, of, Subject, switchMap, tap } from 'rxjs';
import { ProductService } from '../../../shared/services/product.service';
import { CategoryService } from '../../../shared/services/category.service';
import { Category } from '../../../shared/models/category.model';
import { Paginated, Product } from '../../../shared/models/product.model';
import { toUiProduct } from '../../../shared/utils/product.utils';
import { resolvePdpCategorySlug } from '../../catalog/data/catalog-mappers';
import { RecentSearchesStore } from './recent-searches.store';

export type SearchStatus = 'idle' | 'loading' | 'success' | 'empty' | 'error';

const MIN_CHARS = 2;
const DROPDOWN_LIMIT = 5;

/**
 * Orquestador global de la barra de búsqueda del header. Expone signals que
 * cualquier componente (dropdown/sheet) puede leer sin suscribirse, y ofrece
 * métodos imperativos para los handlers del input.
 *
 * Contratos importantes:
 *  - `setQuery` se llama en CADA tecla (actualiza signal + preemptea status).
 *  - `submitQuery` se llama en el evento debounced (400ms) — dispara HTTP.
 *  - `commitSearch` se llama en Enter o click de "Ver todos" — navega a /buscar.
 *  - `switchMap` interno cancela requests en vuelo al teclear más rápido.
 *  - SSR-safe: si hay SSR de la home, el overlay no se abre en server (lo gobierna
 *    el componente vía `@defer (when isOpen())`), así que este service no toca
 *    DOM ni localStorage en server (RecentSearchesStore tiene su propio guard).
 */
@Injectable({ providedIn: 'root' })
export class SearchOverlayService {
  private readonly productService = inject(ProductService);
  private readonly categoryService = inject(CategoryService);
  private readonly router = inject(Router);
  private readonly recentStore = inject(RecentSearchesStore);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _query = signal('');
  private readonly _status = signal<SearchStatus>('idle');
  private readonly _results = signal<Product[]>([]);
  private readonly _totalCount = signal(0);
  private readonly _highlightedIndex = signal(-1);
  private readonly _isOpen = signal(false);
  private readonly _isMobile = signal(false);
  private readonly _recent = signal<string[]>(this.recentStore.load());

  readonly query = this._query.asReadonly();
  readonly status = this._status.asReadonly();
  readonly results = this._results.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  readonly highlightedIndex = this._highlightedIndex.asReadonly();
  readonly isOpen = this._isOpen.asReadonly();
  /** True cuando el overlay debe renderizarse como sheet mobile a pantalla
   *  completa (con su propio input) en vez de dropdown de escritorio. */
  readonly isMobile = this._isMobile.asReadonly();
  readonly recent = this._recent.asReadonly();

  readonly hasQuery = computed(() => this._query().trim().length >= MIN_CHARS);

  private readonly rootsSig = toSignal(this.categoryService.getRootCategoriesCached(), {
    initialValue: [] as Category[],
  });

  private readonly input$ = new Subject<string>();
  private lastQueried = '';

  constructor() {
    this.input$
      .pipe(
        switchMap((term) => {
          this._status.set('loading');
          return this.productService
            .getProducts({
              search: term,
              per_page: DROPDOWN_LIMIT,
              page: 1,
              sort: 'popular',
              skipZeroStock: true,
            })
            .pipe(
              tap((res: Paginated<Product>) => this.onResults(res)),
              catchError((err) => {
                console.error('[search-overlay] fetch failed', err);
                this._status.set('error');
                this._results.set([]);
                this._totalCount.set(0);
                this._highlightedIndex.set(-1);
                return of(null);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /** Actualización sincrónica del signal de query (cada tecla). No dispara HTTP. */
  setQuery(term: string): void {
    this._query.set(term);
    const trimmed = term.trim();
    if (trimmed.length < MIN_CHARS) {
      this.lastQueried = '';
      this._status.set('idle');
      this._results.set([]);
      this._totalCount.set(0);
      this._highlightedIndex.set(-1);
    } else if (this._status() === 'idle') {
      // Preemptive: el usuario está escribiendo algo relevante. Mostrar
      // skeleton sin esperar al debounce evita un flash "idle → loading".
      this._status.set('loading');
    }
  }

  /** Dispara la búsqueda (invocado desde el evento `search` debounced). */
  submitQuery(term: string): void {
    const trimmed = term.trim();
    this._query.set(term);
    if (trimmed.length < MIN_CHARS) {
      this.lastQueried = '';
      this._status.set('idle');
      this._results.set([]);
      this._totalCount.set(0);
      this._highlightedIndex.set(-1);
      return;
    }
    if (trimmed === this.lastQueried && this._status() !== 'error') return;
    this.lastQueried = trimmed;
    this.input$.next(trimmed);
  }

  /** Enter / click "Ver todos" — persiste recent, navega a /buscar, cierra overlay. */
  commitSearch(term: string): void {
    const trimmed = term.trim();
    if (trimmed.length < MIN_CHARS) return;
    const updated = this.recentStore.push(trimmed);
    this._recent.set(updated);
    this.close();
    this.router.navigate(['/buscar'], { queryParams: { q: trimmed } });
  }

  /** Selecciona una búsqueda reciente: commit directo sin pasar por el input. */
  commitRecent(term: string): void {
    this._query.set(term);
    this.commitSearch(term);
  }

  removeRecent(term: string): void {
    this._recent.set(this.recentStore.remove(term));
  }

  clearRecent(): void {
    this._recent.set(this.recentStore.clear());
  }

  /** Lo setea SearchOverlayComponent desde su matchMedia. Vive en el service
   *  para que el header pueda consultarlo (p. ej. al decidir si el blur del
   *  search-bar debe cerrar el overlay). */
  setMobile(value: boolean): void {
    this._isMobile.set(value);
  }

  open(): void {
    this._isOpen.set(true);
    // Refrescar recents por si otro tab los modificó.
    this._recent.set(this.recentStore.load());
  }

  close(): void {
    this._isOpen.set(false);
    this._highlightedIndex.set(-1);
  }

  clear(): void {
    this._query.set('');
    this._results.set([]);
    this._totalCount.set(0);
    this._highlightedIndex.set(-1);
    this._status.set('idle');
    this.lastQueried = '';
  }

  retry(): void {
    const trimmed = this._query().trim();
    if (trimmed.length < MIN_CHARS) return;
    this.lastQueried = '';
    this.submitQuery(trimmed);
  }

  /** ↑/↓ cycling con wrap. delta = -1 arriba, +1 abajo. */
  moveHighlight(delta: -1 | 1): void {
    const total = this._results().length;
    if (total === 0) {
      this._highlightedIndex.set(-1);
      return;
    }
    const current = this._highlightedIndex();
    // -1 significa "nada resaltado": primer ↓ va a 0, primer ↑ va a último.
    if (current === -1) {
      this._highlightedIndex.set(delta === 1 ? 0 : total - 1);
      return;
    }
    const next = (current + delta + total) % total;
    this._highlightedIndex.set(next);
  }

  setHighlight(index: number): void {
    this._highlightedIndex.set(index);
  }

  /** Enter: si hay fila resaltada navega al producto; si no, commit de query. */
  selectHighlighted(): void {
    const idx = this._highlightedIndex();
    const items = this._results();
    if (idx >= 0 && idx < items.length) {
      this.navigateToProduct(items[idx]);
      return;
    }
    this.commitSearch(this._query());
  }

  navigateToProduct(product: Product): void {
    this.close();
    const slug = product.slug ?? String(product.id);
    // Si el producto no trae categoría (caso patológico — el backend siempre
    // las embebe), no navegamos: mejor que llevar a una URL rota.
    const categorySlug = resolvePdpCategorySlug(product, this.rootsSig());
    if (!categorySlug) return;
    this.router.navigate(['/tienda', categorySlug, slug]);
  }

  private onResults(res: Paginated<Product>): void {
    const list = (res.data ?? []).map(toUiProduct);
    this._results.set(list);
    this._totalCount.set(res.total ?? list.length);
    this._status.set(list.length === 0 ? 'empty' : 'success');
    this._highlightedIndex.set(-1);
  }
}
